import React, { useCallback, useRef, useState } from 'react';
import { Alert, Button, Card, Icon, Radio, Statistic, Tag, Tooltip, message } from 'antd';

import { setDeclarationFigure, upsertDailyBalance } from '../../utils/registerRecon/registerDB';
import { dailyTargetKey } from '../../utils/registerRecon/reconEngine';
import { fromCl } from '../../utils/registerRecon/registerNormalize';
import { ExportableTable, fmtL, strSorter } from '../../utils/tableKit';

/*
 * Finding the day it broke.
 *
 * Newest day first, because that is where a reviewer starts: today does not
 * match, so walk back until you reach a day that does. Everything up to and
 * including that day can be trusted; the day after it is where the problem is.
 *
 * One number per day — the tank's closing balance, or the fuel issued at a site
 * with no tank. A day left blank is MISSING: no difference is computed for it and
 * it is not evidence of agreement.
 *
 * Corrections recorded against entries are applied from their own date forward,
 * so fixing an entry five days back visibly pulls every later day into line.
 */

const money = (v) => (v === null || v === undefined ? '—' : fmtL(v));
const L = (cl) => (cl === null || cl === undefined ? null : fromCl(cl));
const BODY_HEIGHT = 520;

function DailyBalancePanel({ recon, dayWiseFor, periodKey, scopeKey, periodTo, targetFocus, onChanged, onGoToEntries }) {
  // Direct fuel is broken down by day like anything else. Before it was, a
  // difference on the direct total had nowhere to be traced to.
  const targets = recon.targets;
  const [targetKey, setTargetKey] = useState(targets.length ? targets[0].key : null);

  // Sent here from a specific figure on Balances? Open on that one.
  const focusRef = useRef(null);
  if (targetFocus && focusRef.current !== targetFocus) {
    focusRef.current = targetFocus;
    if (targets.some((t) => t.key === targetFocus)) setTargetKey(targetFocus);
  }

  const active = targets.find((t) => t.key === targetKey) || targets[0] || null;
  const daily = active ? dayWiseFor(active) : null;
  // A tank carries its balance forward; a flow does not. Everything below that
  // differs between the two hangs off this one distinction.
  const isBalance = !!daily && daily.measure === 'BALANCE';
  const isDirect = !!daily && daily.flowKind === 'DIRECT';

  const [draft, setDraft] = useState({});
  const [dirty, setDirty] = useState({});
  const [savedAt, setSavedAt] = useState(null);

  const seedKey = `${periodKey}|${scopeKey}|${active ? active.key : ''}|${daily ? daily.rows.length : 0}`;
  const seededRef = useRef(null);
  if (seededRef.current !== seedKey) {
    seededRef.current = seedKey;
    const next = {};
    if (daily) daily.rows.forEach((r) => { next[r.date] = L(r.regCl); });
    setDraft(next);
    setDirty({});
  }

  const patch = useCallback((date, value) => {
    setDraft((d) => ({ ...d, [date]: value }));
    setDirty((d) => ({ ...d, [date]: true }));
  }, []);

  // `quiet` is used by the Enter key: saving after every number should not throw
  // a toast up thirty times while someone types down a column. The Save button
  // going grey is the signal instead.
  const save = (quiet = false) => {
    const dates = Object.keys(dirty).filter((d) => dirty[d]);
    if (!dates.length) {
      if (!quiet) message.info('Nothing to save.');
      return;
    }
    try {
      const target = dailyTargetKey(active);
      dates.forEach((date) => {
        const valueLtr = draft[date] === undefined ? null : draft[date];
        upsertDailyBalance({ periodKey, scopeKey, target, date, valueLtr });
        // A tank's last-day closing IS the period closing, so editing it here
        // moves the Balances tab too — one number on two screens.
        if (isBalance && date === periodTo) {
          setDeclarationFigure({ periodKey, scopeKey, target, valueLtr });
        }
      });

      // A FLOW'S PERIOD FIGURE IS THE SUM, AND ONLY WHEN EVERY DAY IS IN
      // ===============================================================
      // For fuel issued, the period total is every day added up — not the last
      // day, which is what a balance uses. So it can only be written once all of
      // them are entered: summing a half-filled column would quietly understate
      // the total and read as a difference that is really just missing days.
      if (!isBalance) {
        const values = daily.rows.map((r) => {
          const d = draft[r.date];
          return d === undefined ? L(r.regCl) : d;
        });
        if (values.length && values.every((v) => v !== null && v !== undefined && v !== '')) {
          setDeclarationFigure({
            periodKey,
            scopeKey,
            target,
            valueLtr: values.reduce((a, v) => a + Number(v), 0),
          });
        }
      }
      setDirty({});
      setSavedAt(Date.now());
      if (!quiet) message.success(`Saved ${dates.length} day(s).`);
      onChanged();
    } catch (e) {
      message.error(e.message);
    }
  };

  // Enter saves and drops into the next day's box, so a column can be typed
  // straight down without reaching for the mouse.
  const onKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    save(true);
    const boxes = [...document.querySelectorAll('[data-recon-day]')];
    const i = boxes.indexOf(e.target);
    const next = i >= 0 ? boxes[i + 1] : null;
    if (next) { next.focus(); next.select(); }
    else e.target.blur();
  };

  if (!active || !daily) {
    return <Alert type="info" showIcon message="Nothing in scope to break down by day." />;
  }


  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      width: 130,
      sorter: strSorter('date'),
      defaultSortOrder: 'descend',
      render: (v, r) => (
        <span>
          {v}
          {!r.entered && <Tag color="red" style={{ marginLeft: 6 }}>blank</Tag>}
        </span>
      ),
    },
    {
      title: <span>Per the register <Tag color="orange">enter</Tag></span>,
      key: 'reg',
      width: 160,
      render: (v, r) => (
        <input
          type="number"
          className="ant-input ant-input-sm"
          data-recon-day={r.date}
          style={{ width: 130, textAlign: 'right', fontFamily: 'monospace' }}
          value={draft[r.date] === null || draft[r.date] === undefined ? '' : draft[r.date]}
          onChange={(e) => patch(r.date, e.target.value === '' ? null : Number(e.target.value))}
          onKeyDown={onKeyDown}
        />
      ),
    },
    ...(isBalance ? [
      { title: 'Received', dataIndex: 'apRecvCl', width: 100, align: 'right', render: (v) => <span style={{ fontFamily: 'monospace', color: '#888' }}>{money(L(v))}</span> },
      { title: 'Issued', dataIndex: 'apIssCl', width: 100, align: 'right', render: (v) => <span style={{ fontFamily: 'monospace', color: '#888' }}>{money(L(v))}</span> },
      {
        title: (
          <Tooltip title="Built from the tank's own movements: the previous day's closing plus this day's receipts, less its issues. The API returns no per-day balance, and the balances it does store have drifted from its own transactions, so the movements are used.">
            <span>Per AssetPro <Icon type="info-circle" /></span>
          </Tooltip>
        ),
        dataIndex: 'apCl',
        width: 140,
        align: 'right',
        render: (v, r) => (
          <div>
            <span style={{ fontFamily: 'monospace', color: '#888' }}>{money(L(v))}</span>
            {r.apChainOk === false && (
              <Tooltip title="AssetPro's own stored balance for this day disagrees with what its movements add up to. The drift is inside AssetPro, so checking the register here will not explain it — and this day will not match the app's own Bowser Day-wise Report.">
                <Icon type="warning" style={{ color: '#fa8c16', marginLeft: 4 }} />
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        title: (
          <Tooltip title="Worked out from the last day you entered, plus the movements since. That is what makes the difference below mean 'went wrong on this day' rather than 'gap carried down from earlier'.">
            <span>Expected <Tag color="blue">from your last entry</Tag> <Icon type="info-circle" /></span>
          </Tooltip>
        ),
        dataIndex: 'expectedCl',
        width: 180,
        align: 'right',
        render: (v, r) => (
          <div>
            <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{money(L(v))}</span>
            <div style={{ fontSize: 11, color: '#bbb' }}>
              {r.anchorDate ? `from ${r.anchorDate}` : 'no earlier entry — AssetPro used'}
            </div>
          </div>
        ),
      },
    ] : []),
    ...(isBalance ? [] : [{
      title: isDirect ? 'Direct per AssetPro' : 'Issued per AssetPro',
      dataIndex: 'apCl',
      width: 160,
      align: 'right',
      render: (v, r) => (
        <div>
          <div style={{ fontFamily: 'monospace' }}>{money(L(v))}</div>
          {r.adjustedApCl !== r.apCl && (
            <div style={{ fontFamily: 'monospace', color: '#1890ff', fontSize: 12 }}>
              {money(L(r.adjustedApCl))} <Tag color="blue">adjusted</Tag>
            </div>
          )}
        </div>
      ),
    }]),
    {
      title: (
        <Tooltip title={isBalance
          ? 'Expected minus what you entered: what went wrong ON THIS DAY. A day that shows nothing is a day where AssetPro and the register moved together, even if a gap from earlier is still being carried.'
          : "AssetPro's total for the day minus what you entered. Each day stands on its own, so this is what went wrong on this day and nothing else."}>
          <span>Difference <Icon type="info-circle" /></span>
        </Tooltip>
      ),
      dataIndex: 'adjustedDiffCl',
      width: 175,
      align: 'right',
      render: (v, r) => {
        if (v === null) {
          return (
            <Tooltip title={r.entered ? 'AssetPro has no balance for this day.' : 'Not entered. Missing, not zero.'}>
              <span style={{ color: '#ccc' }}>—</span>
            </Tooltip>
          );
        }
        const carried = r.carriedDiffCl;
        const showCarried = carried !== null && Math.abs(carried - v) > 1;
        return (
          <div>
            {r.matches
              ? <Tag color="green"><Icon type="check" /> matches</Tag>
              : <Tag color="red" style={{ fontFamily: 'monospace', fontSize: 14 }}>{money(L(v))}</Tag>}
            {showCarried && (
              <Tooltip title="The plain gap against AssetPro's own balance, which includes whatever was carried down from earlier days.">
                <div style={{ fontSize: 11, color: '#bbb' }}>carried {money(L(carried))}</div>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: '',
      key: 'act',
      width: 130,
      render: (v, r) => (r.matches === false
        ? <Button size="small" onClick={() => onGoToEntries(r.date, active.key)}>Check entries</Button>
        : null),
    },
  ];

  const verdict = () => {
    if (!daily.comparable) {
      return (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Fill in the days, newest first"
          description={isBalance
            ? "One number a day: the tank's closing balance that day per the register. Each day is measured from the last one you entered, so a day showing a difference is a day where the two actually moved apart — not one carrying a gap down from earlier."
            : `One number a day: the total ${isDirect ? 'bought and put straight into a machine' : 'issued'} that day per the register. Each day stands on its own — nothing is carried forward — so a day that differs is the day it happened.`}
        />
      );
    }
    if (daily.allMatch) {
      return (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 12 }}
          message={isBalance ? 'Every day you have filled in moved together' : 'Every day you have filled in agrees'}
          description={daily.gaps
            ? `${daily.gaps} day(s) are still blank, so the break may be in one of those — keep going back.`
            : isBalance
              ? 'No day created a difference. If the period still does not tally, the gap pre-dates it.'
              : 'Every day matches, so the period total matches too.'}
        />
      );
    }
    const broke = daily.breakDays || [];
    return (
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message={broke.length === 1
          ? `The difference was created on ${broke[0]}`
          : `A difference was created on ${broke.length} days: ${broke.slice().reverse().slice(0, 5).join(', ')}${broke.length > 5 ? '…' : ''}`}
        description={
          <div>
            <div style={{ marginBottom: 8 }}>
              {isBalance
                ? `Each day is measured from the last day you entered, so these are the days where AssetPro and the register actually moved apart — not days carrying a gap down from earlier.${daily.gaps > 0 ? ` ${daily.gaps} day(s) are still blank; filling in the day before a flagged one will confirm it.` : ''}`
                : `Each day stands on its own, so these are the days the two totals differ. Open one to check the ${isDirect ? 'fills' : 'entries'} behind it.${daily.gaps > 0 ? ` ${daily.gaps} day(s) are still blank and could be hiding more.` : ''}`}
            </div>
            {daily.breakDate && (
              <Button type="primary" size="small" onClick={() => onGoToEntries(daily.breakDate, active.key)}>
                Check the entries for {daily.breakDate}
              </Button>
            )}
          </div>
        }
      />
    );
  };

  return (
    <div>
      {targets.length > 1 && (
        <div style={{ marginBottom: 10 }}>
          <Radio.Group size="small" value={active.key} onChange={(e) => setTargetKey(e.target.value)}>
            {targets.map((t) => (
              <Radio.Button key={t.key} value={t.key}>
                {t.kind === 'TANK_CLOSING' ? t.label.replace(' closing balance', '')
                  : t.kind === 'DIRECT' ? 'Direct to machines' : t.label}
                {t.reconciled === false && <Icon type="exclamation-circle" style={{ color: '#f5222d', marginLeft: 6 }} />}
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>
      )}

      {verdict()}

      <Card size="small" style={{ marginBottom: 12 }} bodyStyle={{ padding: '8px 14px' }}>
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'center' }}>
          <Statistic title="Days filled in" value={daily.entered} suffix={`/ ${daily.rows.length}`} valueStyle={{ fontSize: 18 }} />
          <Statistic title="Days still blank" value={daily.gaps} valueStyle={{ fontSize: 18, color: daily.gaps ? '#faad14' : '#52c41a' }} />
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: '#999', marginRight: 12, textAlign: 'right' }}>
            {Object.values(dirty).some(Boolean)
              ? <span style={{ color: '#fa8c16' }}>Unsaved changes — press Enter or Save</span>
              : savedAt ? 'Saved' : 'Press Enter in a box to save and move to the next day'}
            <div style={{ fontSize: 11, color: '#bbb' }}>
              {isBalance
                ? 'The last day’s balance is the period closing on Balances.'
                : daily.gaps
                  ? `Balances takes the sum of every day — ${daily.gaps} still blank, so it is left as entered.`
                  : 'Balances takes the sum of every day.'}
            </div>
          </span>
          <Button type="primary" icon="save" disabled={!Object.values(dirty).some(Boolean)} onClick={() => save(false)}>Save</Button>
        </div>
      </Card>

      <ExportableTable
        columns={columns}
        dataSource={daily.rows}
        rowKey="key"
        size="small"
        scroll={{ x: isBalance ? 1240 : 760, y: BODY_HEIGHT }}
        pagination={{ pageSize: 31, showSizeChanger: true, pageSizeOptions: ['31', '62', '100'] }}
        exportName={`daily-${active.key}.csv`}
        exportHeader={isBalance
          ? ['Date', 'Entered', 'Register', 'Received', 'Issued', 'Per AssetPro', 'Anchored on', 'Expected', 'Difference this day', 'Gap vs AssetPro']
          : ['Date', 'Entered', 'Register', 'Per AssetPro', 'AssetPro adjusted', 'Difference']}
        exportRow={(r) => (isBalance
          ? [r.date, r.entered ? 'yes' : 'blank', L(r.regCl), L(r.apRecvCl), L(r.apIssCl),
            L(r.apCl), r.anchorDate || '', L(r.expectedCl), L(r.adjustedDiffCl), L(r.carriedDiffCl)]
          : [r.date, r.entered ? 'yes' : 'blank', L(r.regCl), L(r.apCl), L(r.adjustedApCl), L(r.adjustedDiffCl)])}
      />
    </div>
  );
}

export default DailyBalancePanel;
